// hooks/useFeatureFlag.js
// Fetches feature flags once on app load, caches in module scope.
// Usage: const workpapersEnabled = useFeatureFlag('workpapers_enabled')
//        const { flags, loading } = useFeatureFlags()

import { useState, useEffect } from 'react';

const API_URL = import.meta.env.VITE_API_URL || '';

// ─── Module-level cache (survives re-renders, resets on full page reload) ───
let flagCache = null;
let fetchPromise = null;

async function fetchFlags() {
  if (flagCache) return flagCache;
  if (fetchPromise) return fetchPromise;

  fetchPromise = fetch(`${API_URL}/api/config/feature-flags`)
    .then(r => r.json())
    .then(data => {
      flagCache = data;
      fetchPromise = null;
      return data;
    })
    .catch(() => {
      // Safe defaults — never break the app if flags endpoint is down
      fetchPromise = null;
      flagCache = {
        workpapers_enabled:          true,
        bank_statement_enabled:      true,
        signoff_enabled:             true,
        xbrl_export_enabled:         false,
        consolidations_enabled:      false,
        benchmark_report_enabled:    false,
        intake_portal_enabled:       false,
        compliance_calendar_enabled: false,
        management_accounts_enabled: false,
        deferred_tax_enabled:        false,
        additional_documents_enabled:false,
        // SMS provider isn't connected yet — must default OFF here too,
        // since this fallback only fires if the flags endpoint itself is
        // unreachable, and unknown flags elsewhere in this file default
        // to true (see useFeatureFlag() below), which would be the wrong
        // direction for this one specifically.
        sms_otp_backup_enabled:      false,
      };
      return flagCache;
    });

  return fetchPromise;
}

/** Returns all feature flags as an object. */
export function useFeatureFlags() {
  const [flags, setFlags] = useState(flagCache || {});
  const [loading, setLoading] = useState(!flagCache);

  useEffect(() => {
    fetchFlags().then(f => {
      setFlags(f);
      setLoading(false);
    });
  }, []);

  return { flags, loading };
}

/** Returns the boolean value of a single flag. Defaults to true for unknown flags. */
export function useFeatureFlag(flagName) {
  const { flags, loading } = useFeatureFlags();
  if (loading) return true;           // optimistic while loading
  if (!(flagName in flags)) return true; // unknown flags default ON
  return !!flags[flagName];
}

/** Invalidate cache — call after admin saves feature flags */
export function invalidateFlagCache() {
  flagCache = null;
  fetchPromise = null;
}

export default useFeatureFlag;
